import { useState, useEffect } from "react";
import { FileDown, Send } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { money, fmtDate, rollForward } from "./ClientesModule";

const VERTICE = {
  nombre: "Grupo Vértice CR",
  cedula: "3-102-939708",
  tipo: "Sociedad de Responsabilidad Limitada",
  correo: "grupoverticecr@gmail.com",
  telefono: "8596-7408",
  direccion: "Puntarenas, Esparza, Espíritu Santo, 175 metros este del MAG, Edificio Enlace de Negocios",
};

// Rueda una fecha mes a mes (conservando el día) hasta hoy o futuro: las fechas de cobro son recurrentes
export function fechaCobro(billing) {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const rolled = [billing?.pay_date_1, billing?.pay_date_2]
    .map((p, i) => ({ f: rollForward(p), cual: i + 1 }))
    .filter((x) => x.f)
    .sort((a, b) => a.f.localeCompare(b.f));
  if (!rolled.length) return { fecha: null, cual: 0 };
  const futura = rolled.find((x) => new Date(x.f + "T00:00:00") >= hoy) || rolled[0];
  return { fecha: futura.f, cual: futura.cual };
}

export async function descargarPdf(elementId, filename) {
  const el = document.getElementById(elementId);
  const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const imgW = pageW - 20;
  const pxPerMm = canvas.width / imgW;
  const pagePxH = Math.floor((pageH - 20) * pxPerMm);
  let offset = 0;
  let page = 0;
  while (offset < canvas.height) {
    const sliceH = Math.min(pagePxH, canvas.height - offset);
    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = sliceH;
    slice.getContext("2d").drawImage(canvas, 0, offset, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL("image/png"), "PNG", 10, 10, imgW, sliceH / pxPerMm);
    offset += sliceH;
    page++;
  }
  pdf.save(filename);
}

export async function descargarReciboPdf(elementId, numero) {
  await descargarPdf(elementId, `Recibo-${numero}.pdf`);
}

export function ReciboDocumento({ d, id }) {
  return (
    <div id={id} data-testid={id} className="relative bg-white text-slate-800 p-10 overflow-hidden" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Marca de agua */}
      <img src="/assets/recurso-26.png" alt="" aria-hidden="true"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] opacity-[0.06] pointer-events-none select-none" />

      {/* Encabezado */}
      <div className="relative flex items-start justify-between gap-6 pb-6 border-b-2 border-[#0f2a43]">
        <img src="/assets/recurso-24.webp" alt="Grupo Vértice CR" className="h-16 w-auto" />
        <div className="text-right">
          <p className="text-[#0d9488] font-bold tracking-[0.2em] text-sm uppercase">Recibo de Cobro</p>
          <p className="text-slate-500 text-xs mt-1">N° {d.numero}</p>
          <p className="text-slate-500 text-xs">Emitido: {d.emitido}</p>
        </div>
      </div>

      {/* Emisor / Cliente */}
      <div className="relative grid sm:grid-cols-2 gap-6 py-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#0d9488] font-semibold mb-2">Emisor</p>
          <p className="font-bold text-[#0f2a43] text-lg leading-tight">{VERTICE.nombre}</p>
          <p className="text-xs text-slate-600 mt-1.5">Cédula jurídica: {VERTICE.cedula}</p>
          <p className="text-xs text-slate-600">{VERTICE.tipo}</p>
          <p className="text-xs text-slate-600 mt-1.5">Correo: {VERTICE.correo}</p>
          <p className="text-xs text-slate-600">Teléfono: {VERTICE.telefono}</p>
          <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{VERTICE.direccion}</p>
        </div>
        <div className="sm:text-right">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#0d9488] font-semibold mb-2">Cobrar a</p>
          <p className="font-bold text-[#0f2a43] text-lg leading-tight">{d.company || "—"}</p>
          {d.ced && <p className="text-xs text-slate-600 mt-1.5">Cédula jurídica: {d.ced}</p>}
          {d.client_email && <p className="text-xs text-slate-600">Correo: {d.client_email}</p>}
        </div>
      </div>

      {/* Detalle del cobro */}
      <div className="relative rounded-lg border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-12 bg-[#0f2a43] text-white text-[11px] uppercase tracking-[0.15em] font-semibold">
          <p className="col-span-6 px-4 py-3">Concepto</p>
          <p className="col-span-3 px-4 py-3">Fecha de cobro</p>
          <p className="col-span-3 px-4 py-3 text-right">Monto</p>
        </div>
        {d.items.map((item, i) => (
          <div key={i} className="grid grid-cols-12 text-sm border-b border-slate-200">
            <p className="col-span-6 px-4 py-4 text-slate-700">{item.concepto}</p>
            <p className="col-span-3 px-4 py-4 text-slate-700">{item.fecha}</p>
            <p className="col-span-3 px-4 py-4 text-right font-semibold text-slate-800">{money(item.monto, d.currency)}</p>
          </div>
        ))}
        {d.invoice && (
          <div className="grid grid-cols-12 text-sm border-b border-slate-200">
            <p className="col-span-6 px-4 py-3 text-slate-500">IVA (13%)</p>
            <p className="col-span-3 px-4 py-3" />
            <p className="col-span-3 px-4 py-3 text-right text-slate-700">{money(d.iva, d.currency)}</p>
          </div>
        )}
        <div className="grid grid-cols-12 bg-slate-50">
          <p className="col-span-6 px-4 py-4 text-[11px] uppercase tracking-[0.2em] font-bold text-[#0f2a43]">Total a cobrar</p>
          <p className="col-span-3 px-4 py-4" />
          <p className="col-span-3 px-4 py-4 text-right text-xl font-bold text-[#0d9488]" data-testid={`${id}-total`}>{money(d.total, d.currency)}</p>
        </div>
      </div>

      {/* Pie */}
      <div className="relative pt-6 mt-2 text-center">
        <p className="text-xs text-slate-500">Gracias por su confianza. Este recibo fue generado desde el panel administrativo de Grupo Vértice CR.</p>
        <p className="text-[10px] text-slate-400 mt-1">{VERTICE.correo} · Tel. {VERTICE.telefono}</p>
      </div>
    </div>
  );
}

export default function ReciboCobro({ client, billing, open, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [fechaSel, setFechaSel] = useState("");

  const hoy = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (open && billing) {
      setFechaSel(fechaCobro(billing).fecha || hoy);
    }
  }, [open, billing]);

  if (!client || !billing?.amount) return null;

  const fecha = fechaSel || hoy;
  const cual = billing.pay_date_2 && fecha.slice(8, 10) === billing.pay_date_2.slice(8, 10) ? 2 : 1;
  const base = billing.frequency === "quincenal" ? billing.amount / 2 : billing.amount;
  const iva = billing.invoice ? base * 0.13 : 0;
  const numero = `VCR-${fecha.replaceAll("-", "")}-${(billing.client_code || String(client.id).slice(-4)).toUpperCase()}`;
  const concepto = `Mensualidad de Servicios Profesionales Interdisciplinarios${billing.frequency === "quincenal" ? ` · ${cual === 1 ? "I" : "II"} Quincena` : ""}`;

  const d = {
    numero,
    emitido: fmtDate(hoy),
    company: client.company_name,
    ced: client.ced_juridica,
    client_email: client.owner_email,
    items: [{ concepto, fecha: fmtDate(fecha), monto: base }],
    invoice: billing.invoice,
    currency: billing.currency || "CRC",
    iva,
    total: base + iva,
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await descargarReciboPdf("recibo-cobro", numero);
    } finally {
      setDownloading(false);
    }
  };

  const sendReceipt = async () => {
    setSending(true);
    try {
      await api.post(`/admin/billing/${client.system}/${client.id}/send-receipt`, { fecha });
      toast.success(`Recibo enviado al correo del cliente, con copia a Grupo Vértice CR.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar el recibo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-vnavy-800 border-white/10 text-white max-w-3xl" data-testid="recibo-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Recibo de Cobro</DialogTitle>
          <DialogDescription className="text-gray-500">
            Vista previa del recibo para {client.company_name}. Puede descargarlo en PDF o enviarlo al correo del cliente con copia a Grupo Vértice CR.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 flex-wrap rounded-lg border border-white/10 px-4 py-3 mb-3">
          <label className="text-sm text-gray-400">Período de cobro:</label>
          <input type="date" value={fechaSel} onChange={(e) => setFechaSel(e.target.value)} data-testid="recibo-fecha-cobro"
            className="h-9 rounded-md border border-white/15 bg-vnavy px-3 text-sm text-white [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-vteal-light/50" />
          <span className="text-gray-600 text-xs">Se sugiere la próxima fecha del plan; puede cambiarla antes de enviar.</span>
        </div>

        <div className="overflow-y-auto max-h-[65vh] rounded-xl">
          <ReciboDocumento d={d} id="recibo-cobro" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={downloadPdf} disabled={downloading} data-testid="recibo-pdf-btn"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full border border-vteal-light/40 text-vteal-light text-sm font-semibold hover:bg-vteal-light/10 transition-colors disabled:opacity-60">
            <FileDown className="h-4 w-4" /> {downloading ? "Generando..." : "Descargar PDF"}
          </button>
          <button onClick={sendReceipt} disabled={sending} data-testid="recibo-send-btn"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-vteal-light text-vnavy text-sm font-semibold hover:bg-white transition-colors disabled:opacity-60">
            <Send className="h-4 w-4" /> {sending ? "Enviando..." : "Enviar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
